import { differenceInCalendarDays, format, formatDistanceToNowStrict, parseISO } from "date-fns";

export function cnJoin(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

const asDate = (value: string | Date) => (typeof value === "string" ? parseISO(value) : value);

export function formatDate(value: string | Date | null | undefined, pattern = "EEE, MMM d"): string {
  if (!value) return "—";
  return format(asDate(value), pattern);
}

export function formatLongDate(value: string | Date | null | undefined): string {
  return formatDate(value, "MMMM d, yyyy");
}

export function relativeDay(value: string, today: string): string {
  const diff = differenceInCalendarDays(parseISO(value), parseISO(today));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff < 0) return `${-diff} days ago`;
  if (diff < 7) return format(parseISO(value), "EEEE");
  return formatDate(value);
}

export function timeAgo(value: string | null | undefined): string {
  if (!value) return "";
  return `${formatDistanceToNowStrict(parseISO(value))} ago`;
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(digits)}%`;
}

export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  lecture_video: "Lecture video",
  course_site: "Course website",
  open_textbook: "Open textbook",
  lecture_notes: "Lecture notes",
  paper: "Academic paper",
  video: "Video",
  documentation: "Documentation",
  interactive: "Interactive exercise",
  open_source_project: "Open-source project",
  problem_set: "Problem set",
  dataset: "Dataset",
  article: "Article",
  other: "Other",
};

export const ASSESSMENT_TYPE_LABELS: Record<string, string> = {
  quiz: "Quiz",
  math_exercise: "Maths exercise",
  coding_exercise: "Coding exercise",
  research_notebook: "Research notebook",
  written_explanation: "Written explanation",
  case_study: "Case study",
  practical_project: "Practical project",
  video_presentation: "Video presentation",
  teach_back: "Teach-back",
  self_reflection: "Self-reflection",
};

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Today's date in the browser's timezone (YYYY-MM-DD) — never the UTC date. */
export function localToday(): string {
  return format(new Date(), "yyyy-MM-dd");
}
